import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetActiveEnterpriseQuery,
  useGetEnterpriseSolutionStatsMutation,
  useGetTrackCompanyListQuery,
  useGetUpdateViewedTimeMutation,
} from "../../../../store/Company/companyApiSlice";
import { setEnterprise } from "../../../../store/enterprise/enterpriseSlice";
import { CommonToast } from "../../../common/Toast";
import { debounce } from "../../../../utils/Helper";
import StatsOverview from "../../Dashboard/StatsOverview";
import AddCompaniesSection from "../../Dashboard/AddCompaniesSection";
import ContactSupportCard from "../../../common/ContactSupportCard";

import { EnterpriseHeader } from "./EnterpriseHeader";
import { MemberBenefitCard } from "./MemberBenefitCard";
import { TrackedBusinessesCard } from "./TrackedBusinessesCard";
import { EnterpriseEmptyState } from "./EnterpriseEmptyState";

/* u1 Partner Program. Visual uplift only. Everything about what this screen does is TRANSCRIBED from... */

/* Transcribed: the live screen requests nine at a time. */
export const PAGE_LIMIT = 9;

/* Transcribed verbatim from checkCredit. A partner program has no credit
   ceiling; everything else counts down, and the singular is spelled out. */
export const creditLabel = (enterprise) => {
  if (enterprise?.enterprise_type === "PARTNER_PROGRAM") return "Unlimited Credits";
  const credits = enterprise?.credits;
  if (credits && credits > 1) return `${credits} Credits`;
  if (credits === 1) return `${credits} Credit`;
  return "0 Credit";
};

export const EnterprisePanel = () => {
  const { token } = useSelector((state) => state.auth);
  const { selectedEnterprise } = useSelector((state) => state.enterprise);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  /* What the field shows, kept apart from the term the server is asked for —
     the live screen debounces between the two and so does this. */
  const [queryInput, setQueryInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState(null);

  const { data: enterpriseData, error } = useGetActiveEnterpriseQuery({ token });
  const [getEnterpriseSolutionStats] = useGetEnterpriseSolutionStatsMutation();

  const enterprises = useMemo(() => enterpriseData?.results || [], [enterpriseData]);

  /* Transcribed: a failure on the enterprise list is surfaced as a toast. */
  useEffect(() => {
    if (error) CommonToast.error(error.message || "Something went wrong");
  }, [error]);

  /* Transcribed: the first enterprise is selected for you if none is. */
  useEffect(() => {
    if (!selectedEnterprise && enterprises.length > 0) {
      dispatch(setEnterprise(enterprises[0]));
    }
  }, [enterprises, selectedEnterprise, dispatch]);

  const { data: tracked, refetch: refetchTracked } = useGetTrackCompanyListQuery(
    { token, id: selectedEnterprise?.id, page, limit: PAGE_LIMIT, searchTerm },
    { skip: !selectedEnterprise?.id }
  );

  const [markUpdatesViewed] = useGetUpdateViewedTimeMutation();

  /* Transcribed: the stats panel is fetched per enterprise. */
  useEffect(() => {
    if (!selectedEnterprise?.id) return;
    let cancelled = false;
    getEnterpriseSolutionStats({ token, id: selectedEnterprise.id })
      .then((res) => {
        if (!cancelled) setStats(res?.data);
      })
      /* The live screen awaits this without a catch, so a rejection becomes an
         unhandled promise. The panel simply stays empty instead — it is
         supplementary, and there is nothing to tell the user to do. */
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedEnterprise?.id, token, getEnterpriseSolutionStats]);

  /* Transcribed: the search term reaches the server debounced, so typing does
     not fire a request per keystroke. Created once — a debounce rebuilt on
     every render never delays anything. */
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const pushSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      /* A narrowed list has fewer pages, so staying on page 4 would ask the
         server for a page that no longer exists. */
      setPage(1);
    }),
    []
  );

  const onQueryChange = (value) => {
    setQueryInput(value);
    pushSearch(value);
  };

  /* Transcribed: the name opens the public profile in-app, not a new tab. */
  const openProfile = (id) => navigate(`/search/${id}`);

  /* Opening a row's changes marks them seen — the same mutation, with the same argument shape, that the activity log fires on mount. */
  const openChanges = (row) => {
    if (!row?.id) return;
    markUpdatesViewed({ token, id: row.id });
  };

  /* The refresh the activity log does immediately, deferred to the way out —
     so the count is right once the reader has finished with it. */
  const closeChanges = () => {
    refetchTracked();
  };

  const hasEnterprise = enterprises.length > 0;
  const rows = tracked?.results || [];
  const total = tracked?.total_results ?? 0;
  const totalPages = tracked?.total_pages ?? 1;
  const isPartnerProgram = selectedEnterprise?.enterprise_type === "PARTNER_PROGRAM";

  /* Transcribed: an enterprise with nothing tracked yet gets the upload and
     support pair instead of the table. A search that matches nothing is NOT
     that state — the table says so itself. */
  const nothingTracked = rows.length === 0 && !searchTerm;

  return (
    <div className="min-w-0">
      <EnterpriseHeader
        enterprises={enterprises}
        selected={selectedEnterprise}
        credits={hasEnterprise ? creditLabel(selectedEnterprise) : undefined}
        onSelect={(next) => {
          dispatch(setEnterprise(next));
          /* A different enterprise is a different list; page 4 of the last
             one means nothing here. */
          setPage(1);
        }}
      />

      {!hasEnterprise ? (
        <EnterpriseEmptyState className="mt-5" />
      ) : (
        <>
          <MemberBenefitCard className="mt-5" />

          {/* {isPartnerProgram && ( <div className="mt-[22px]"> <StatsOverview enterpriseStats={stats} /> </div> )} */}

          {nothingTracked ? (
            <div className="mt-[22px]">
              <AddCompaniesSection enterprise={selectedEnterprise} />
              <ContactSupportCard />
            </div>
          ) : (
            <TrackedBusinessesCard
              className="mt-[22px]"
              rows={rows}
              total={total}
              page={page}
              totalPages={totalPages}
              pageSize={PAGE_LIMIT}
              query={queryInput}
              onQueryChange={onQueryChange}
              onPageChange={setPage}
              onOpenProfile={openProfile}
              onOpenChanges={openChanges}
              onCloseChanges={closeChanges}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EnterprisePanel;
